import {
  enums,
  enumsTypes,
  optionsByPK,
  PAYMENT_TYPE_OPTIONS_MAP,
} from "@shared/constants";
import { CurrencyMapIdType } from "@shared/constants/enums/enums-types";

import { DiscoverySubItemSerializer } from "@app-contexts/public/features/discovery/data/types/discovery";
import { ProductVariantSerializer } from "@app-contexts/public/features/products/data/types/products";
import { DEFAULT_CURRENCY } from "@shared/constants/financials";
import {
  CLPTicketTypeSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import { PayWhatYouCanConfiguration } from "@shared/modules/events/types";
import { PublicMembershipLevelSerializer } from "@shared/modules/memberships/types";
import Cookies from "js-cookie";
import { FeesPricingInfoDetails, FeesPricingInfoType } from "../types";

// all item types available for sale on Showpass
type AllItemsType =
  | (TicketTypeSerializer & { $pwycPrice?: number })
  | (ProductVariantSerializer & { $pwycPrice?: number })
  | (PublicMembershipLevelSerializer & { $pwycPrice?: number })
  | (CLPTicketTypeSerializer & { $pwycPrice?: number })
  | (DiscoverySubItemSerializer & { $pwycPrice?: number });

type AllItemsArrayType =
  | (TicketTypeSerializer & { $pwycPrice?: number })[]
  | (ProductVariantSerializer & { $pwycPrice?: number })[]
  | (CLPTicketTypeSerializer & { $pwycPrice?: number })[]
  | (DiscoverySubItemSerializer & { $pwycPrice?: number })[]
  | (ProductVariantSerializer & { $pwycPrice?: number })[]
  | (PublicMembershipLevelSerializer & { $pwycPrice?: number })[]
  | undefined;

// use the router locale to format price strings
export const getPriceLocale = () => Cookies.get("NEXT_LOCALE") || "en";

/**
 * Used internally by other price util functions.
 * For displaying pricing, see CurrencyFormat.
 *
 *
 * Returns prices with a currency symbol, thousand separator, and X decimal places
 */
export const getFormattedPrice = ({
  inputNum,
  currency = DEFAULT_CURRENCY,
  decimalPlaces,
  locale,
  includeSuffix,
}: {
  inputNum: number;
  currency: CurrencyMapIdType;
  decimalPlaces: number;
  locale?: string;
  includeSuffix?: boolean;
}) => {
  const localeToUse = locale ?? getPriceLocale();

  const priceFormatter = Intl.NumberFormat(localeToUse, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });

  return priceFormatter
    .format(inputNum)
    .concat(includeSuffix ? ` ${currency}` : "");
};

// checks for fees object typing where all of the fields are exposed/accessible
// example: data type returned from the calendar details endpoint
export const isExposedPricingInfo = (
  feesPricingInfo?: FeesPricingInfoType | FeesPricingInfoDetails | null
): feesPricingInfo is FeesPricingInfoDetails =>
  Boolean(
    feesPricingInfo &&
      "total_price_no_tax" in feesPricingInfo &&
      "total_price" in feesPricingInfo &&
      "service_charges" in feesPricingInfo &&
      "taxes" in feesPricingInfo &&
      "sum_custom_fees" in feesPricingInfo &&
      "sum_internal_fees" in feesPricingInfo
  );

// checks for fees object typing with the format: fees[purchase_platform][payment_type]
// example: data type returned from non-calendar endpoints
export const isNestedPricingInfo = (
  feesPricingInfo?:
    | FeesPricingInfoType
    | FeesPricingInfoDetails
    | CLPTicketTypeSerializer["configured_fees_pricing_info"]
    | null,
  cartPSP?: string | null,
  cartPaymentType?: number | null
): feesPricingInfo is FeesPricingInfoType => {
  let returnVal = false;

  if (feesPricingInfo && cartPSP && cartPaymentType) {
    const allPSPTypes = Object.keys(enums.PURCHASE_SOURCE_PLATFORM_MAP_BY_ID);
    const allPaymentTypes = Object.keys(
      optionsByPK(PAYMENT_TYPE_OPTIONS_MAP)
    ).map((pt) => Number(pt));

    if (
      allPSPTypes.includes(cartPSP) &&
      allPaymentTypes.includes(cartPaymentType) &&
      feesPricingInfo[cartPSP] &&
      feesPricingInfo[cartPSP][cartPaymentType]
    ) {
      returnVal = true;
    }
  }

  return returnVal;
};

// gets the item's base price or the all-in-pricing value
export const getItemPrice = (
  itemPrice: string,
  hasAllInPricing: boolean,
  feesPricingInfo?: FeesPricingInfoType | FeesPricingInfoDetails,
  cartPaymentType?: number,
  cartPSP?: string,
  autoDiscountAmount?: number,
  payWhatYouCanConfiguration?: PayWhatYouCanConfiguration | null
): string => {
  let returnPrice = itemPrice;
  // handle undefined values
  if (!feesPricingInfo || !cartPSP || !cartPaymentType) {
    return returnPrice;
  }
  if (payWhatYouCanConfiguration) {
    const allInPricingServiceCharges =
      hasAllInPricing &&
      isNestedPricingInfo(feesPricingInfo, cartPSP, cartPaymentType)
        ? feesPricingInfo[cartPSP][cartPaymentType].service_charges
        : "0";
    // if all in pricing is enabled, add the service charges to the return price (pwyc selection)
    return (
      Number(allInPricingServiceCharges) + Number(returnPrice)
    ).toString();
  }

  if (hasAllInPricing) {
    const discount = autoDiscountAmount ?? 0;
    let totalPriceNoTax: string | undefined;

    // check for typing from non-calendar endpoints
    if (isNestedPricingInfo(feesPricingInfo, cartPSP, cartPaymentType)) {
      totalPriceNoTax =
        feesPricingInfo[cartPSP][cartPaymentType]?.total_price_no_tax;
    }
    // check for typing from calendar details endpoint
    else if (isExposedPricingInfo(feesPricingInfo)) {
      totalPriceNoTax = feesPricingInfo?.total_price_no_tax;
    }

    returnPrice = (
      parseFloat(totalPriceNoTax || itemPrice) - discount
    ).toString();
  }
  return returnPrice;
};

/**
 * Returns the difference between two price strings
 * @param price1 The first price string to compare
 * @param price2 The second price string to compare. This value is subtracted from price1.
 * @returns An integer (number) representing the difference between the two prices
 */
export function getPriceDifference(price1: string, price2: string): number {
  // Convert the price strings to floats, ensuring proper handling of negatives
  const parsedPrice1 = parseFloat(price1);
  const parsedPrice2 = parseFloat(price2);

  // If parsing fails, return NaN (you can choose to throw an error or handle it differently)
  if (Number.isNaN(parsedPrice1) || Number.isNaN(parsedPrice2)) {
    throw new Error("Invalid price string provided");
  }

  // Calculate the difference between the two prices
  const difference = parsedPrice1 - parsedPrice2;

  return difference;
}

export const displayAllInPricingComponent = (
  itemPrice: string | undefined,
  hasAllInPricing: boolean,
  feesPricingInfo?: FeesPricingInfoType | FeesPricingInfoDetails,
  cartPaymentType?: number,
  cartPSP?: string
): boolean => {
  let returnValue = false;

  // handle undefined values
  if (!feesPricingInfo || !cartPaymentType || !cartPSP || !itemPrice) {
    return returnValue;
  }

  // check for typing from non-calendar endpoints
  if (isNestedPricingInfo(feesPricingInfo, cartPSP, cartPaymentType)) {
    returnValue =
      (parseFloat(feesPricingInfo[cartPSP][cartPaymentType]?.service_charges) >
        0 ||
        parseFloat(feesPricingInfo[cartPSP][cartPaymentType]?.taxes) > 0) &&
      parseFloat(itemPrice) > 0 &&
      hasAllInPricing;
  }
  // check for typing from calendar details endpoint
  else if (isExposedPricingInfo(feesPricingInfo)) {
    returnValue =
      (parseFloat(feesPricingInfo?.service_charges) > 0 ||
        parseFloat(feesPricingInfo?.taxes) > 0) &&
      parseFloat(itemPrice) > 0 &&
      hasAllInPricing;
  }

  return returnValue;
};

export const sortItemPrices = (
  a: AllItemsType,
  b: AllItemsType,
  purchasePlatform: string,
  paymentType: number,
  allInPricing: boolean
): number => {
  let a_fees_pricing_info:
    | FeesPricingInfoType
    | CLPTicketTypeSerializer["configured_fees_pricing_info"];
  let b_fees_pricing_info:
    | FeesPricingInfoType
    | CLPTicketTypeSerializer["configured_fees_pricing_info"];

  if ("fees_pricing_info" in a) {
    a_fees_pricing_info = a.fees_pricing_info;
  } else if ("configured_fees_pricing_info" in a) {
    a_fees_pricing_info = a.configured_fees_pricing_info;
  }

  if ("fees_pricing_info" in b) {
    b_fees_pricing_info = b.fees_pricing_info;
  } else if ("configured_fees_pricing_info" in b) {
    b_fees_pricing_info = b.configured_fees_pricing_info;
  }

  // make sure these values exist before sorting
  if (
    isNestedPricingInfo(a_fees_pricing_info, purchasePlatform, paymentType) &&
    isNestedPricingInfo(b_fees_pricing_info, purchasePlatform, paymentType)
  ) {
    const total_price_of_a = parseFloat(
      a_fees_pricing_info[purchasePlatform][paymentType]?.total_price_no_tax
    );
    const total_price_of_b = parseFloat(
      b_fees_pricing_info[purchasePlatform][paymentType]?.total_price_no_tax
    );

    if (allInPricing && total_price_of_a && total_price_of_b) {
      return total_price_of_a - total_price_of_b;
    }
  }

  return parseFloat(a.price ?? "") - parseFloat(b.price ?? "");
};

// Returns the price as a floating point number
export const getPrice = (
  item: AllItemsType,
  purchasePlatform: string,
  paymentType: number,
  allInPricing: boolean,
  payWhatYouCanPrice?: number
): number => {
  // If PWYC price is provided and item has PWYC configuration, use it
  if (
    payWhatYouCanPrice !== undefined &&
    (item as TicketTypeSerializer).pay_what_you_can_configuration
  ) {
    return payWhatYouCanPrice;
  }

  let fees_pricing_info:
    | FeesPricingInfoType
    | CLPTicketTypeSerializer["configured_fees_pricing_info"];

  if ("fees_pricing_info" in item) {
    fees_pricing_info = item.fees_pricing_info;
  } else if ("configured_fees_pricing_info" in item) {
    fees_pricing_info = item.configured_fees_pricing_info;
  }

  if (
    allInPricing &&
    isNestedPricingInfo(fees_pricing_info, purchasePlatform, paymentType)
  ) {
    return parseFloat(
      fees_pricing_info[purchasePlatform][paymentType]?.total_price_no_tax
    );
  }

  return parseFloat(item.price ?? "");
};

export type GetPriceRangeOptions = {
  items: AllItemsArrayType;
  allInPricing: boolean;
  venueCurrency: enumsTypes.CurrencyMapIdType;
  paymentType?: number;
  purchasePlatform?: string;
  shouldRound?: boolean;
  roundingStrategy?: "default" | "up" | "down";
};

// Accepts an input of items of any type (tickets, products, memberships, etc)
// If there are items with different prices, it returns a price range in this format:
// ${smallestVal} CURR - ${largestVal} CURR
// If all items have the same price, it returns a single price in this format:
// ${value with 2 decimals} CURR
export const getPriceRange = ({
  items,
  allInPricing,
  venueCurrency,
  paymentType = PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
  purchasePlatform = enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
  shouldRound = true,
  roundingStrategy = "up", // we always default to rounding up the prices
}: GetPriceRangeOptions): string => {
  if (!items?.length) {
    return "";
  }

  items.sort((a, b) =>
    sortItemPrices(a, b, purchasePlatform, paymentType, allInPricing)
  );

  const lowestPriceNum = getPrice(
    items[0],
    purchasePlatform,
    paymentType,
    allInPricing,
    items[0].$pwycPrice
  );

  const highestPriceNum = getPrice(
    items[items.length - 1],
    purchasePlatform,
    paymentType,
    allInPricing,
    items[items.length - 1].$pwycPrice
  );

  // Helper function to apply rounding strategy
  const applyRounding = (price: number): number => {
    if (!shouldRound) return price;

    switch (roundingStrategy) {
      case "up":
        return Math.ceil(price);
      case "down":
        return Math.floor(price);
      case "default":
      default:
        return Math.round(price);
    }
  };

  // Determine comparison logic based on shouldRound and strategy
  const roundedLowest = applyRounding(lowestPriceNum);
  const roundedHighest = applyRounding(highestPriceNum);

  const pricesAreEqual = shouldRound
    ? roundedLowest === roundedHighest
    : lowestPriceNum === highestPriceNum;

  // Case where there's only one item type, so lowest price == highest price
  if (pricesAreEqual) {
    // For single price, always show 2 decimal places
    return Number.isFinite(lowestPriceNum)
      ? `${getFormattedPrice({
          inputNum: lowestPriceNum,
          currency: venueCurrency,
          decimalPlaces: 2,
          includeSuffix: true,
        })}`
      : "";
  }

  // For price ranges
  if (shouldRound) {
    // Use rounding strategy with 0 decimal places
    return Number.isFinite(lowestPriceNum) && Number.isFinite(highestPriceNum)
      ? `${getFormattedPrice({
          inputNum: roundedLowest,
          currency: venueCurrency,
          decimalPlaces: 0,
          includeSuffix: false,
        })} - ${getFormattedPrice({
          inputNum: roundedHighest,
          currency: venueCurrency,
          decimalPlaces: 0,
          includeSuffix: true,
        })}`
      : "";
  }

  // No rounding: exact prices with 2 decimal places
  return Number.isFinite(lowestPriceNum) && Number.isFinite(highestPriceNum)
    ? `${getFormattedPrice({
        inputNum: lowestPriceNum,
        currency: venueCurrency,
        decimalPlaces: 2,
        includeSuffix: false,
      })} - ${getFormattedPrice({
        inputNum: highestPriceNum,
        currency: venueCurrency,
        decimalPlaces: 2,
        includeSuffix: true,
      })}`
    : "";
};

export const convertMoneyStringToNumber = (moneyInput: string | undefined) => {
  if (typeof moneyInput === "string") {
    // Remove any currency symbols and comma separators
    const normalizedString = moneyInput.replace(/[^0-9.-]+/g, "");

    // Convert the cleaned string to a floating point number
    const number = parseFloat(normalizedString);

    // Check if the parsed number is a valid number
    if (Number.isNaN(number)) {
      return undefined;
    }

    // Return the number as is
    return number;
  }

  // Return undefined if the input is neither a string nor a number
  return undefined;
};

/**
 * Converts a string representing a price and converts it into a number
 *
 * For example, '25.00' becomes 2500.
 *
 * Payment processors like Stripe strictly use whole integers to mitigate the
 * risks when it comes to rounding -- a behavior that can vary depending on platform and language.
 *
 * Our system uses strings though.
 */
export function convertPriceStringToInteger(price: string) {
  return Number(price.replace(".", ""));
}

/**
 * Converts a number string to its percentage representation without floating-point errors.
 * NOTE: In most cases you will want the backend to do any math on financials.
 * Use this mainly for display purposes where exact precision is not necessary since the
 * limitations with the integer scaling method are with large numbers, long decimals, and scientific notation.
 *
 * ⚠️  IMPORTANT: This function only accepts strings to avoid floating-point precision issues.
 * ⚠️  DO NOT pass the result of mathematical operations directly.
 *
 * ✅ Good: convertToPercent("0.07")
 * ✅ Good: convertToPercent(userInput) // from form input
 * ✅ Good: convertToPercent(value.toString()) // when you know value is safe
 *
 * ❌ Bad: convertToPercent(0.07 * 100) // precision error!
 * ❌ Bad: convertToPercent(0.1 + 0.2)  // precision error!
 *
 * @param {string} numberString The number as a string (e.g., "0.07", "12.34567").
 * @returns {number} The accurate percentage representation (e.g., 7, 1234.567).
 */
export function convertToPercent(numberString: string) {
  if (
    typeof numberString !== "string" ||
    Number.isNaN(parseFloat(numberString))
  ) {
    throw new Error("Invalid input: must be a numeric string.");
  }

  const [integerPart, fractionalPart = ""] = numberString.split(".");

  const scaledInteger = parseInt(integerPart + fractionalPart, 10);

  const originalDecimals = fractionalPart.length;
  const scaleFactor = 10 ** originalDecimals;

  return (scaledInteger * 100) / scaleFactor;
}

/**
 * Converts a percentage string back to its number representation without floating-point errors.
 * NOTE: In most cases you will want the backend to do any math on financials.
 * Use this mainly for display purposes where exact precision is not necessary since the
 * limitations with the integer scaling method are with large numbers, long decimals, and scientific notation.
 *
 * ⚠️  IMPORTANT: This function only accepts strings to avoid floating-point precision issues.
 * ⚠️  DO NOT pass the result of mathematical operations directly.
 *
 * ✅ Good: convertToNumberFromPercent("7")
 * ✅ Good: convertToNumberFromPercent(userInput) // from form input
 * ✅ Good: convertToNumberFromPercent(value.toString()) // when you know value is safe
 *
 * ❌ Bad: convertToNumberFromPercent(0.07 * 100) // precision error!
 * ❌ Bad: convertToNumberFromPercent(0.1 + 0.2)  // precision error!
 *
 * @param {string} percentString The percentage as a string (e.g., "7", "1234.567").
 * @returns {number} The accurate number representation (e.g., 0.07, 12.34567).
 */
export function convertToNumberFromPercent(percentString: string) {
  if (typeof percentString !== "string") {
    throw new Error("Invalid input: must be a string");
  }

  const cleanedString = percentString.trim();
  if (Number.isNaN(parseFloat(cleanedString))) {
    throw new Error("Invalid input: must be a numeric string.");
  }

  const [integerPart, fractionalPart = ""] = cleanedString.split(".");

  const scaledInteger = parseInt(integerPart + fractionalPart, 10);

  const originalDecimals = fractionalPart.length;
  const finalScaleFactor = 10 ** originalDecimals * 100;

  return scaledInteger / finalScaleFactor;
}
